/**
 * The one owner for what a person reads (rd round 12, lane A4).
 *
 * WHY THIS EXISTS (rc-3 "what a person reads"). The console knows every
 * company's display name in the shell, but at least eleven pages build their
 * own name map, four places invent a "name" with `slug.toUpperCase()`, the
 * unknown-company stand-in is spelled three ways, and Worker sentences quote
 * the internal slug. Round 12 fixes the cause once: every word that could be a
 * slug, an account path or a Worker sentence is built HERE, and wave B moves
 * the pages onto these functions.
 *
 * Owner decision 14 (2026-10-31, orchestrator's recommendation accepted with
 * "Your recs are good."): "When a company's name is unknown, sentences use
 * "this company" and page headings use "Company". The slug is never shown."
 *
 * WHAT LIVES HERE
 *   - `CompanyDirectory` / `createCompanyDirectory`: the shell's one name map.
 *   - `face.company`: the ONLY supplier of the unknown-company stand-in.
 *   - `face.account`: the one account face; delegates to the twin-aware
 *     helpers in `ui/display-words.ts`, so the account-label guard keeps
 *     holding.
 *   - `ariaFor`: the words an aria-label or title must carry, built from the
 *     same words as the visible text.
 *   - `face.checklistStep` / `face.receiptWords`: portal-built onboarding
 *     words, never the Worker's slug-bearing strings.
 *
 * WHAT IT CANNOT DO. It cannot stop a caller printing a slug or a path through
 * a template; `rd-r12-a4-brand-guard.test.ts` is what makes that fail. It
 * cannot know a name the directory was not given; an incomplete directory
 * (`complete: false`) still returns null and the caller reads "this company"
 * rather than guessing. And `face.receiptWords` recognises the receipt shapes
 * this console can produce; an unknown shape FAILS CLOSED to one fixed
 * sentence, with the Worker's own text carried in `details` for the "Show
 * details" disclosure only (round 2, HIGH 1: a token scrubber cannot tell a
 * slug from a word, so it neither cleaned the leaks nor left the rest alone).
 */

import { amVisibleDisplayText } from "../../../ingest-worker/src/am_visible_text.js";
import type {
  AccountPath,
  CompanySlug,
  OnboardingReceipt,
  OnboardingStepKey,
  ReceiptDetails,
  StaffRole,
} from "../types.js";

import {
  COMPANY_UNKNOWN_TEXT,
  accountLabelAmong,
  type AccountLabelContext,
} from "./display-words.js";
import { checklistKeyLabel } from "./plan_summary.js";

/** The page-heading stand-in (owner decision 14). Never a slug. */
export const COMPANY_HEADING_UNKNOWN_TEXT = "Company";

/** The possessive stand-in (owner decision 14). */
export const COMPANY_POSSESSIVE_UNKNOWN_TEXT = "this company's";

/**
 * The shell's names for companies, keyed by slug.
 *
 * `name` returns only names that exist — it never guesses a name from the slug
 * — so a real name that equals its slug ignoring case ("Acme", "Syn-Rentals")
 * is still shown. `complete` is the caller's honest answer to "did this read
 * see every company?"; an incomplete directory is a valid value, and a caller
 * that wants to say so reads the flag rather than fabricating a name.
 */
export interface CompanyDirectory {
  name(slug: CompanySlug): string | null;
  has(slug: CompanySlug): boolean;
  /**
   * Every company this directory can name, by slug. The source of the one
   * account-label context (`accountLabelContext`), so a page that holds a
   * directory never builds a second, empty one (rd13 L1).
   */
  names(): ReadonlyMap<CompanySlug, string>;
  /**
   * The ledger segment each company's accounts are written under, mapped to
   * its plain name, when the session read them (`IntercompanySnapshot`,
   * `face.companySegmentNames`). Empty means "not read": a label then falls
   * back to the slug-derived segment. rd13 L1, review round 2.
   */
  segments(): ReadonlyMap<string, string>;
  complete: boolean;
}

/** One company row the shell already read (name is staff-entered text). */
export interface CompanyDirectoryRow {
  slug: CompanySlug;
  displayName: string | null;
}

/** Whitespace-collapsed, hazard-stripped display text; "" when unusable. */
function cleanWords(value: string | null | undefined): string {
  return amVisibleDisplayText(value ?? "")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Build the one directory from the shell's company rows. `opts.complete` is
 * the honest coverage flag; it is carried, never inferred.
 */
export function createCompanyDirectory(
  rows: readonly CompanyDirectoryRow[],
  opts: {
    complete: boolean;
    /** The session's segment read, if it has one (`companySegmentNames`). */
    segments?: ReadonlyMap<string, string>;
  },
): CompanyDirectory {
  const names = new Map<CompanySlug, string>();
  const known = new Set<CompanySlug>();
  for (const row of rows) {
    known.add(row.slug);
    const name = cleanWords(row.displayName);
    if (name !== "") names.set(row.slug, name);
  }
  return {
    name: (slug) => names.get(slug) ?? null,
    // A row with no name is still a company we know about; `has` answers "is
    // this slug one of ours", never "do we have words for it".
    has: (slug) => known.has(slug),
    names: () => names,
    segments: () => opts.segments ?? new Map<string, string>(),
    complete: opts.complete,
  };
}

/**
 * The ledger segment each known company writes its accounts in, mapped to that
 * company's plain name (rd13 L1).
 *
 * THE AUTHORITATIVE SEGMENT IS `IntercompanySnapshot.companies[].segment`
 * (rd13 L1, review round 2). A company's segment is a free uppercase code in
 * the company's own `company.yaml` (`contracts/ledger-conventions.md`), so the
 * console CANNOT derive it: `syn-trust` writes under `TRST`, and a slug rule
 * would look for `SYNTRUST` and read a real company's account as "another
 * company". The shell reads the intercompany snapshot once per session and
 * hands the answer to `companySegmentNames`; only a session with no such read
 * falls back to `companySegments` below, whose slug derivation matches
 * `pipeline/ic_pairs.py` `segment_for_slug` and is right only when the company
 * kept the default code.
 *
 * A guessed WRONG name is never allowed, so once the read has answered it is
 * the only source: a segment it does not list reads "another company" rather
 * than being derived from a slug that may belong to a different company.
 */
export function companySegmentNames(
  companies: readonly { slug: CompanySlug; segment: string }[] | null | undefined,
  labels: ReadonlyMap<CompanySlug, string> | undefined,
): Map<string, string> {
  const segments = new Map<string, string>();
  for (const row of companies ?? []) {
    const segment = cleanWords(row.segment);
    const name = cleanWords(labels?.get(row.slug));
    if (segment === "" || name === "") continue;
    segments.set(segment, name);
  }
  return segments;
}

/**
 * The slug-derived fallback: `sor-properties` -> `SORPROPERTIES`, mapped to the
 * company's plain name. Used only when the session could not read the
 * authoritative segments (`companySegmentNames`).
 */
export function companySegments(
  labels: ReadonlyMap<CompanySlug, string> | undefined,
): AccountLabelContext {
  const segments = new Map<string, string>();
  for (const [slug, name] of labels ?? []) {
    const clean = cleanWords(name);
    if (clean === "") continue;
    const segment = segmentForSlug(slug);
    if (segment !== "") segments.set(segment, clean);
  }
  return segments;
}

/**
 * A company's ledger segment: `sor-properties` -> `SORPROPERTIES`
 * (`pipeline/ic_pairs.py` `segment_for_slug`, lockstep). The value is an
 * internal key — it is matched against an account's own segment and is never
 * words a person reads — so the slug crosses the sanctioned slug-to-key
 * boundary (`slugText`/`accountText` in `console/report-render.ts`) first and
 * is only then transformed; a branded slug never becomes text.
 */
function segmentForSlug(slug: CompanySlug): string {
  const key = new Map<CompanySlug, string>([[slug, slug]]).get(slug) ?? "";
  return key.replace(/-/g, "").toUpperCase();
}

/**
 * THE account-label context for a page, built ONCE from the company names the
 * page already holds (rd13 L1): the shell's `companyLabels`, or
 * `directory.names()`.
 *
 * Every label call on the page then takes this value, and the label helpers
 * take it as a required argument, so a page cannot name an account without
 * saying which company each ledger segment is. Before this existed the map was
 * an optional parameter and only the Statements page passed it; the To review
 * picker, Rules, Months, Compare, the receipt shelf and the card face all
 * printed "Due from TRST" and "Repairs (RENT)".
 *
 * `authoritative` is the session's read of the real segments
 * (`companySegmentNames`, from `GET /v1/admin/intercompany`). When it holds
 * any segment it is the ONLY naming source; the slug derivation is the
 * fallback for a session that has no such read (review round 2, item 1). A
 * `CompanyDirectory` carries both facts, so a page that has one passes it and
 * the segments travel with the names.
 */
export function accountLabelContext(
  source?: CompanyDirectory | ReadonlyMap<CompanySlug, string>,
  authoritative?: ReadonlyMap<string, string> | null,
): AccountLabelContext {
  const labels = isDirectory(source) ? source.names() : source;
  const read = isDirectory(source) ? source.segments() : authoritative;
  if (read !== undefined && read !== null && read.size > 0) {
    const named = new Map<string, string>();
    for (const [segment, name] of read) {
      const clean = cleanWords(name);
      if (clean === "") continue;
      named.set(segment, clean);
    }
    if (named.size > 0) return named;
  }
  return companySegments(labels);
}

/** A `CompanyDirectory` rather than a bare slug -> name map. */
function isDirectory(
  source: CompanyDirectory | ReadonlyMap<CompanySlug, string> | undefined,
): source is CompanyDirectory {
  return typeof (source as CompanyDirectory | undefined)?.segments === "function";
}

/** `Acme` -> `Acme's`, `Acme Rentals` -> `Acme Rentals'`. */
function possessiveOf(name: string): string {
  return /s$/i.test(name) ? `${name}'` : `${name}'s`;
}

/**
 * A company as a person reads it. The unknown forms are owner decision 14:
 * a sentence reads "this company", a heading reads "Company", a possessive
 * reads "this company's". The slug is never returned.
 */
export function company(
  slug: CompanySlug,
  dir: CompanyDirectory,
  as: "sentence" | "heading" | "possessive",
): string {
  const known = dir.name(slug);
  if (known !== null) {
    return as === "possessive" ? possessiveOf(known) : known;
  }
  if (as === "heading") return COMPANY_HEADING_UNKNOWN_TEXT;
  if (as === "possessive") return COMPANY_POSSESSIVE_UNKNOWN_TEXT;
  return COMPANY_UNKNOWN_TEXT;
}

/**
 * A ledger account as a person reads it (`Expenses:Repairs` ->
 * `Expenses › Repairs`, `Assets:Checking:Operating` -> `Checking › Operating`).
 * Delegates to the twin-aware helpers in `ui/display-words.ts`: pass `among`
 * (the accounts the company offers) and two accounts that would read the same
 * are told apart, exactly as the account-label guard requires. `companies` is
 * the page's one label context (`accountLabelContext`), and it is REQUIRED: a
 * between-companies account names the company instead of printing its key.
 */
export function account(
  path: AccountPath,
  companies: AccountLabelContext,
  among?: readonly AccountPath[],
): string {
  // Always the set-aware helper: with no set it falls back to the plain label,
  // and with one it tells two twin accounts apart. Routing both cases through
  // it keeps the one-label-set decision in `ui/display-words.ts`.
  return accountLabelAmong(path, among ?? [], companies);
}

/** A whole value shaped like an email address (one @, a dot in the domain). */
const EMAIL_SHAPE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/u;

/**
 * The person who did something, as a person reads it: their email address when
 * the stored identity is one, else null (rd r12 F1, R4). A sign-in id
 * (`demo-operator`, `hub:practice-manager`, a service subject) is not a name a
 * reader knows, so the caller shows nothing on the face and keeps the raw
 * value behind "Show details".
 */
export function person(identity: string | null | undefined): string | null {
  const cleaned = cleanWords(identity);
  return EMAIL_SHAPE.test(cleaned) ? cleaned : null;
}

/**
 * The words an aria-label or title must carry, built from the visible text.
 *
 * A label is the same words as the visible text with the sentence punctuation
 * a control does not need at its end removed; it is never a second wording
 * (`aria-label="Open ..."` on a control that reads "..."), because a screen
 * reader and a sighted reader must be told the same thing. Text remains
 * untrusted API/staff text and is cleaned here like everywhere else.
 */
export function ariaFor(visibleWords: string): string {
  return cleanWords(visibleWords).replace(/[\s.,;:]+$/u, "");
}

/**
 * The human words for one checklist step key. Delegates to the shared
 * `checklistKeyLabel`, so the portal has one title for a step instead of two
 * (the Worker's `title` and a second local map).
 */
export function checklistStep(key: OnboardingStepKey): string {
  return checklistKeyLabel(key);
}

/** Who a receipt or a checklist step is waiting on, in plain words. */
export const WHO_WORDS: Readonly<Record<string, string>> = {
  owner: "the owner",
  am: "the account manager",
  account_manager: "the account manager",
  system: "the system",
  bank_linker: "the person connecting the bank",
  hub_team: "the hub",
  portal_team: "the tenant portal team",
  agent: "the agent",
};

/** The receipt's `next` line for this reader, or null when there is none. */
function receiptNextLine(
  next: { who: string; text: string } | null,
  role: StaffRole,
): string | null {
  if (next === null) return null;
  const who = next.who;
  if (who === "owner") {
    return role === "owner"
      ? "Next: you approve or complete the next step."
      : "Next: the owner approves or completes the next step.";
  }
  if (who === "am" || who === "account_manager") {
    return role === "account_manager"
      ? "Next: you complete the next step."
      : "Next: the account manager completes the next step.";
  }
  const label = WHO_WORDS[who] ?? "someone";
  return `Next: ${label} takes the next step.`;
}

/** A step key from a sentence, only when it is shaped like one. */
function stepLabelFromToken(token: string): string {
  if (!/^[a-z][a-z0-9_]*$/.test(token)) return "a checklist step";
  return checklistStep(token as OnboardingStepKey);
}

/**
 * What a receipt says when this module does not recognise its shape.
 *
 * Round-2 HIGH 1: the old fallback scrubbed slug-shaped tokens out of the
 * Worker's sentence, which leaked every case the pattern did not match
 * (`SYN-RENTALS`, `(syn-rentals)`, `Expenses:Repairs`, a one-word slug) and
 * rewrote ordinary hyphenated words into "this company". A sentence we cannot
 * build is one we do not show: the fixed line below is always true, and the
 * Worker's own words travel in `details`, which only "Show details" renders.
 */
export const RECEIPT_UNKNOWN_TEXT = "Something changed for this company.";

/** The receipt shapes this console can produce, and their plain rebuilt words. */
interface ReceiptShape {
  pattern: RegExp;
  happened: (match: RegExpExecArray, dir: CompanyDirectory) => string;
  unchanged: string;
}

const RECEIPT_SHAPES: readonly ReceiptShape[] = [
  {
    pattern: /^Started an onboarding run for (\S+) in (.+) mode\.$/u,
    happened: (match, dir) =>
      `Started the checklist for ${company(match[1] as CompanySlug, dir, "sentence")}.`,
    unchanged:
      "Nothing else changed: no company, key or bank invitation was created, and no GitHub action ran.",
  },
  {
    pattern: /^Re-checked every step the system can prove for (\S+)\.$/u,
    happened: (match, dir) =>
      `Checked every step the system can prove for ${company(match[1] as CompanySlug, dir, "sentence")}.`,
    unchanged:
      "Nothing was created, issued or invited: this only updated these step checks.",
  },
  {
    pattern: /^Marked the hand step (\S+) done for (\S+)\.$/u,
    happened: (match, dir) =>
      `Marked ${stepLabelFromToken(match[1] ?? "")} done for ${company(
        match[2] ?? "",
        dir,
        "sentence",
      )}.`,
    unchanged:
      "Nothing was created, issued or invited: this records a person's tick on a step the system cannot check.",
  },
  {
    pattern: /^Registered (\S+) from the approved plan\.$/u,
    happened: (match, dir) =>
      `Registered ${company(match[1] as CompanySlug, dir, "sentence")} from the approved plan.`,
    unchanged:
      "No API key, bank invitation or GitHub action ran. The company is the only thing written.",
  },
  {
    pattern: /^Prepared an API key for (\S+); it is ready to reveal\.$/u,
    happened: (match, dir) =>
      `Prepared an API key for ${company(match[1] as CompanySlug, dir, "sentence")}. It is ready to show to the owner.`,
    unchanged: "Nothing else has been issued yet.",
  },
  {
    pattern: /^Prepared a bank-link invitation for (\S+); it is ready to reveal\.$/u,
    happened: (match, dir) =>
      `Prepared a bank invitation for ${company(match[1] as CompanySlug, dir, "sentence")}. It is ready to show to the owner.`,
    unchanged: "Nothing else has been issued yet.",
  },
  {
    pattern: /^Issued an API key for (\S+)\.$/u,
    happened: (match, dir) =>
      `Issued an API key for ${company(match[1] as CompanySlug, dir, "sentence")}.`,
    unchanged: "No other settings changed.",
  },
  {
    pattern: /^Issued a link invitation for (\S+)\.$/u,
    happened: (match, dir) =>
      `Issued a bank invitation for ${company(match[1] as CompanySlug, dir, "sentence")}.`,
    unchanged: "No other settings changed.",
  },
  {
    // `ingest-worker/src/company_registration.ts:1299` sends this shape when an
    // account manager discards a saved company draft.
    pattern: /^Discarded the saved company draft for (\S+)\.$/u,
    happened: (match, dir) =>
      `Discarded the saved draft for ${company(match[1] as CompanySlug, dir, "sentence")}.`,
    unchanged: "No company was registered, and no key or bank invitation was issued.",
  },
];

/** The words a receipt card shows, built in the portal. */
export interface ReceiptWords {
  happened: string;
  unchanged: string;
  next: string | null;
  /**
   * The Worker's own sentence, cleaned but never rebuilt. It goes ONLY in the
   * "Show details" body (support needs raw ids and paths, A4-3); a sentence a
   * person reads comes from `happened` / `unchanged` above. The `ReceiptDetails`
   * brand is what makes the brand guard flag it anywhere else (folded round-2
   * LOW 2); a plain `string` had no marker to see.
   */
  details: ReceiptDetails;
}

/**
 * The words a receipt card shows, built in the portal.
 *
 * The Worker's receipt quotes the internal slug (`Started an onboarding run
 * for syn-rentals in new mode.`), so the card takes the shape, the directory
 * and the step key and rebuilds the sentence; the Worker's text is never
 * copied into `happened`. The company is worded by `face.company`, so an
 * unknown name reads "this company" and the slug is never shown. An unknown
 * shape fails closed to `RECEIPT_UNKNOWN_TEXT`.
 */
export function receiptWords(
  receipt: OnboardingReceipt,
  dir: CompanyDirectory,
  role: StaffRole,
): ReceiptWords {
  const raw = cleanWords(receipt.happened);
  const details = raw;
  for (const shape of RECEIPT_SHAPES) {
    const match = shape.pattern.exec(raw);
    if (match !== null) {
      return {
        happened: shape.happened(match, dir),
        unchanged: shape.unchanged,
        next: receiptNextLine(receipt.next, role),
        details,
      };
    }
  }
  return {
    happened: RECEIPT_UNKNOWN_TEXT,
    // An unrecognised shape cannot tell us what else did or did not change,
    // so the card says only what is always true: something changed here.
    unchanged: "Nothing else changed.",
    next: receiptNextLine(receipt.next, role),
    details,
  };
}
